import { ActionModal } from '@kcftech/design-system';
import {
    GroupAlertsAcknowledgementRequest,
    ILocationResponse,
    ISystemOverviewResponse,
    ISystemResponse,
} from '@kcftech/prometheus-client';
import { useMutation, useQuery } from '@tanstack/react-query';
import { SpinnerArea } from 'components/spinner/SpinnerArea';
import { useNotificationContext } from 'contexts/NotificationContext';
import { sdClient } from 'contexts/SDKClientContext';
import { extractErrorString } from 'utils/api';
import { Filter } from 'utils/query_v3';
import { getItemCountText } from 'utils/string';

export type AcknowledgeAlertsModalProps = Readonly<{
    location: ILocationResponse;
    system?: ISystemOverviewResponse | ISystemResponse;
    onClose: (acknowledged: boolean) => void;
}>;

export function AcknowledgeAlertsModal({
    location,
    system,
    onClose,
}: AcknowledgeAlertsModalProps) {
    const { showToast } = useNotificationContext();

    const entityType = system ? 'system' : 'location';
    const entityName = system?.Name ?? location.Name;

    const { data: alertCount, isLoading: isLoadingAlertCount } = useQuery({
        queryKey: ['acknowledge-alerts-alert-count', system?.Id, location.Id],
        queryFn: () =>
            sdClient.getAlerts({
                pageLimit: 1,
                groupIds: system ? [system.Id] : undefined,
                filter: Filter.and(
                    Filter.eq('LocationId', location.Id),
                    Filter.eq('IsAcknowledged', false),
                ),
            }),
        select: (result) => result.Total,
    });

    const { mutate: acknowledgeAlerts, isPending: isAcknowledgingAlerts } = useMutation({
        mutationFn: () =>
            system
                ? sdClient.acknowledgeAlertsByGroupIds({
                      body: new GroupAlertsAcknowledgementRequest({
                          GroupIds: [system.Id],
                      }),
                  })
                : sdClient.acknowledgeAlertsByLocationId({ locationId: location.Id }),
        onSuccess: () => {
            showToast({
                type: 'success',
                content: `Successfully acknowledged ${entityName} alert(s) and updated the Activity Log.`,
            });
            onClose(true);
        },
        onError: (error) => {
            const errorMessage = extractErrorString(error) ?? 'Unknown error occurred';
            showToast({
                type: 'error',
                content: `Failed to acknowledge ${entityName} alerts: ${errorMessage}`,
            });
        },
    });

    return (
        <ActionModal
            title={`Acknowledge All Alerts for ${entityName}`}
            width={680}
            height={250}
            actionButtonText="Acknowledge All"
            closeButtonText="Close"
            actionable={
                !isLoadingAlertCount &&
                alertCount !== undefined &&
                alertCount > 0 &&
                !isAcknowledgingAlerts
            }
            closable={!isAcknowledgingAlerts}
            onAction={acknowledgeAlerts}
            onClose={() => onClose(false)}
        >
            <SpinnerArea isLoading={isLoadingAlertCount}>
                {!isLoadingAlertCount && alertCount !== undefined && (
                    <div>
                        {alertCount > 0 ? (
                            <>
                                Are you sure you want to acknowledge all{' '}
                                <span className="bold">
                                    {getItemCountText(alertCount, 'alert')}
                                </span>{' '}
                                for this {entityType}? This will remove all existing
                                alerts that have been triggered. New alerts will be
                                triggered upon exceeding their indicator thresholds.
                            </>
                        ) : (
                            <>
                                There are no alerts to be acknowledged for this{' '}
                                {entityType}.
                            </>
                        )}
                    </div>
                )}
            </SpinnerArea>
        </ActionModal>
    );
}
